/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs from "dayjs";
import { Col, Row } from "react-awesome-styled-grid";
import { Else, If, Then } from "react-if";
import { ListLoader, Text, theme } from "@nordcloud/gnui";
import { useGetEventsViewQuery, EventsSortFields } from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import { useQueryState } from "~/hooks";
import { isEmpty, isNotEmpty } from "~/tools";
import { GridColumns } from "../styled";
import { QueryGetEvents } from "./constants";
import { prepareEventsFilterBy } from "./eventFilterBuilders";
import { EventItem } from "./EventItem";
import { EventsLoader } from "./EventLoader";

export function EventsTable() {
  const { queryState } = useQueryState<QueryGetEvents>();

  const sortInput = {
    field: queryState.sortField as EventsSortFields,
    order: queryState.order,
  };

  const { data, loading } = useGetEventsViewQuery({
    variables: {
      page: Number(queryState.page ?? "1"),
      limit: Number(queryState.limit),
      sort: sortInput,
      filter: {
        filterBy: prepareEventsFilterBy(queryState),
        phrase: isNotEmpty(queryState.searchPhrase?.trim() || "")
          ? queryState.searchPhrase
          : undefined,
      },
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "network-only",
  });

  const events = data?.eventsView?.result ?? [];
  const count = data?.eventsView.pageInfo?.count ?? 0;

  const wrongRangeInput = dayjs(queryState.startDateUTC).isAfter(
    queryState.endDateUTC
  );

  return (
    <>
      <LoaderWrap
        loading={loading}
        Component={() => (
          <ListLoader>
            <EventsLoader />
          </ListLoader>
        )}
      >
        <If condition={isEmpty(events)}>
          <Then>
            <If condition={wrongRangeInput}>
              <Then>
                <NoData
                  hasIcon
                  message={
                    "The date range is incorrect. Please ensure that the 'startDate' and 'endDate' parameters in the link are correctly specified."
                  }
                />
              </Then>
              <Else>
                <NoData hasIcon message={"There are no Events."} />
              </Else>
            </If>
          </Then>
          <Else>
            <>
              <Row>
                <Col>
                  <GridColumns hasPadding>
                    <div />
                    <div />
                    <Text
                      size="sm"
                      weight="medium"
                      m={0}
                      color={theme.color.text.text02}
                    >
                      Event Name
                    </Text>
                    <Text
                      size="sm"
                      weight="medium"
                      m={0}
                      color={theme.color.text.text02}
                    >
                      Start Time (local time)
                    </Text>
                    <Text
                      size="sm"
                      weight="medium"
                      m={0}
                      color={theme.color.text.text02}
                    >
                      End Time (local time)
                    </Text>
                    <Text
                      size="sm"
                      weight="medium"
                      m={0}
                      color={theme.color.text.text02}
                    >
                      Event Duration
                    </Text>
                  </GridColumns>
                </Col>
              </Row>
              {events.map((event) => (
                <EventItem key={event.id} event={event} />
              ))}
              <Pagination count={count} />
            </>
          </Else>
        </If>
      </LoaderWrap>
    </>
  );
}
